import { ErrorService } from '@/core/shared/errors/error.service';
import { InviteRequiresWorkEmailError } from '@/core/shared/errors/invite-requires-work-email.error';
import MeService from '@/core/shared/me/me.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { isValidEmail } from '@/utils/forms/is-valid-email.form';
import { type Ref, computed, ref, watch } from 'vue';

const WORK_EMAIL_INVITE_ERROR = 'Please use a work email to invite teammates.';

export function useInviteMember(): {
  isLoading: Ref<boolean>;
  emailInput: Ref<string>;
  isValidEmailInput: Ref<boolean>;
  inviteErrorMessage: Ref<string>;
  inviteSent: Ref<boolean>;
  onInvite: () => Promise<void>;
  resetState: () => void;
} {
  const meService = new MeService();
  const isLoading = ref(false);
  const emailInput = ref('');
  const inviteErrorMessage = ref('');
  const inviteSent = ref(false);
  const isValidEmailInput = computed(() => isValidEmail(emailInput.value));

  watch(emailInput, () => {
    inviteErrorMessage.value = '';
  });

  async function onInvite() {
    try {
      isLoading.value = true;
      inviteErrorMessage.value = '';

      if (!emailInput.value) return;

      const isAlreadyAMember = meService.isEmployerMember(emailInput.value);

      if (isAlreadyAMember) {
        SnackbarService.caution(
          'This email is already in use. Please have your teammate log in to access FactoryFix',
        );
        return;
      }

      await meService.inviteEmployerMember(emailInput.value);
      SnackbarService.success('Invitation sent');
      inviteSent.value = true;
    } catch (error) {
      inviteSent.value = false;

      if (error instanceof InviteRequiresWorkEmailError) {
        inviteErrorMessage.value = WORK_EMAIL_INVITE_ERROR;
        return;
      }

      ErrorService.captureException(error);
      SnackbarService.critical('Something went wrong. Please try again');
    } finally {
      isLoading.value = false;
    }
  }

  function resetState() {
    emailInput.value = '';
    inviteErrorMessage.value = '';
    inviteSent.value = false;
  }

  return {
    isLoading,
    emailInput,
    isValidEmailInput,
    inviteErrorMessage,
    inviteSent,
    onInvite,
    resetState,
  };
}
